import type { components } from '../api/generated/entitlements';

type Item = components['schemas']['CatalogueItemView'];

export type FeatureRow = { name: string; grantedBy: Record<string, boolean> };
export type ModuleView = { slug: string; features: string[]; requiresTier: string | null };
export type OfferView = {
  slug: string;
  confers: { kind: string; slug: string }[];
  durationDays: number | null;
  availability: string | null;
  oncePerOrg: boolean;
};
export type CatalogueMatrix = { tiers: string[]; features: FeatureRow[]; modules: ModuleView[]; offers: OfferView[] };

// Item ids are "<kind>:<slug>"; the slug is everything after the first colon.
export function slugOf(id: string): string {
  const i = id.indexOf(':');
  return i === -1 ? id : id.slice(i + 1);
}

// durationDays is typed number | string | null on the wire; coerce to a finite number or null.
function toFiniteOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// One flat item shape covers all three kinds; `type` says which fields matter. An
// unrecognised `type` (the wire type is a bare string) belongs to no group and is dropped
// — it is not renderable, and isCatalogueEmpty treats a set of only-unknowns as empty.
export function toMatrix(items: Item[]): CatalogueMatrix {
  const tierItems = items.filter((i) => i.type === 'tier');
  const tiers = tierItems.map((t) => slugOf(t.id));

  const featureNames: string[] = [];
  for (const t of tierItems) for (const f of t.features ?? []) if (!featureNames.includes(f)) featureNames.push(f);

  const features: FeatureRow[] = featureNames.map((name) => {
    const grantedBy: Record<string, boolean> = {};
    for (const t of tierItems) grantedBy[slugOf(t.id)] = (t.features ?? []).includes(name);
    return { name, grantedBy };
  });

  const modules: ModuleView[] = items.filter((i) => i.type === 'module').map((m) => ({
    slug: slugOf(m.id), features: m.features ?? [], requiresTier: m.requiresTier ?? null,
  }));

  const offers: OfferView[] = items.filter((i) => i.type === 'trial-offer').map((o) => ({
    slug: slugOf(o.id),
    confers: (o.confers ?? []).map((c) => ({ kind: c.kind ?? '', slug: c.slug ?? '' })),
    durationDays: toFiniteOrNull(o.durationDays),
    availability: o.availability ?? null,
    oncePerOrg: o.oncePerOrg ?? false,
  }));

  return { tiers, features, modules, offers };
}

// Emptiness is a property of what's renderable, not raw item count — an item of an
// unknown kind counts toward neither, so a catalogue of only-unknowns reads as empty.
export function isCatalogueEmpty(m: CatalogueMatrix): boolean {
  return m.tiers.length === 0 && m.modules.length === 0 && m.offers.length === 0;
}

// --- Editing (EIT-85) -------------------------------------------------------

export type ItemStatus = 'available' | 'retired';
export type TierDraft = { slug: string; features: string[]; status?: ItemStatus };
export type ModuleDraft = { slug: string; features: string[]; requiresTier: string | null; status?: ItemStatus };
export type OfferDraft = { slug: string; confers: { kind: string; slug: string }[]; durationDays: number | null; availability: string | null; oncePerOrg: boolean };
export type Draft = { productId: string; tiers: TierDraft[]; modules: ModuleDraft[]; offers: OfferDraft[] };

// Absent or unrecognised status reads as available (EIT-79 AC-4).
function readStatus(i: Item): ItemStatus {
  return i.status === 'retired' ? 'retired' : 'available';
}
// Include status in a body ONLY when retired (the non-default), so an untouched item never
// diffs on status and a retire->restore round-trip is a clean no-op. tierBody, moduleBody and
// baselineBody all apply this identical rule for symmetry.
function withStatus<T extends DefineItemRequestBody>(body: T, status: ItemStatus | undefined): T {
  return status === 'retired' ? { ...body, status } : body;
}

export type DefineItemRequestBody = {
  features?: string[]; requiresTier?: string | null;
  confers?: { kind: string; slug: string }[]; durationDays?: number | null; availability?: string | null; oncePerOrg?: boolean;
  status?: string | null;
};
export type CatalogueWrite = { id: string; productId: string; version: number | string | undefined; body: DefineItemRequestBody };

type Kind = 'tier' | 'module' | 'trial-offer';
const KIND_ORDER: Record<Kind, number> = { tier: 0, module: 1, 'trial-offer': 2 };

// Per-kind item->draft mappers. Shared by toDraft (initial load) and revertChange (restore an
// edited item), so a restored item can never disagree with how it first loaded.
function itemToTier(i: Item): TierDraft {
  return { slug: slugOf(i.id), features: [...(i.features ?? [])], status: readStatus(i) };
}
function itemToModule(i: Item): ModuleDraft {
  return { slug: slugOf(i.id), features: [...(i.features ?? [])], requiresTier: i.requiresTier ?? null, status: readStatus(i) };
}
function itemToOffer(i: Item): OfferDraft {
  return {
    slug: slugOf(i.id), confers: (i.confers ?? []).map((c) => ({ kind: c.kind ?? '', slug: c.slug ?? '' })),
    durationDays: i.durationDays == null ? null : Number(i.durationDays), availability: i.availability ?? null, oncePerOrg: i.oncePerOrg ?? false,
  };
}

export function toDraft(items: Item[], productId: string): Draft {
  const tiers = items.filter((i) => i.type === 'tier').map(itemToTier);
  const modules = items.filter((i) => i.type === 'module').map(itemToModule);
  const offers = items.filter((i) => i.type === 'trial-offer').map(itemToOffer);
  return { productId, tiers, modules, offers };
}

// Revert one item by id: drop it if it has no baseline counterpart (a draft-only add), else
// restore it from the baseline via the shared mappers. Client-only — never a write.
export function revertChange(draft: Draft, baseline: Item[], id: string): Draft {
  const base = baseline.find((i) => i.id === id) ?? null;
  const slug = slugOf(id);
  if (id.startsWith('tier:')) {
    const tiers = base ? draft.tiers.map((t) => (t.slug === slug ? itemToTier(base) : t)) : draft.tiers.filter((t) => t.slug !== slug);
    return { ...draft, tiers };
  }
  if (id.startsWith('module:')) {
    const modules = base ? draft.modules.map((m) => (m.slug === slug ? itemToModule(base) : m)) : draft.modules.filter((m) => m.slug !== slug);
    return { ...draft, modules };
  }
  const offers = base ? draft.offers.map((o) => (o.slug === slug ? itemToOffer(base) : o)) : draft.offers.filter((o) => o.slug !== slug);
  return { ...draft, offers };
}

export function featureGrid(tiers: TierDraft[]): FeatureRow[] {
  const names: string[] = [];
  for (const t of tiers) for (const f of t.features) if (!names.includes(f)) names.push(f);
  return names.map((name) => ({ name, grantedBy: Object.fromEntries(tiers.map((t) => [t.slug, t.features.includes(name)])) }));
}

export function isDraftEmpty(d: Draft): boolean {
  return d.tiers.length === 0 && d.modules.length === 0 && d.offers.length === 0;
}

export type ChangedItem = { id: string; kind: Kind; draftBody: DefineItemRequestBody; base: Item | null };

function tierBody(t: TierDraft): DefineItemRequestBody { return withStatus({ features: t.features }, t.status); }
function moduleBody(m: ModuleDraft): DefineItemRequestBody { return withStatus({ features: m.features, requiresTier: m.requiresTier }, m.status); }
function offerBody(o: OfferDraft): DefineItemRequestBody { return { confers: o.confers, durationDays: o.durationDays, availability: o.availability, oncePerOrg: o.oncePerOrg }; }

function norm(body: DefineItemRequestBody): string {
  const features = body.features ? [...body.features].sort((a, b) => a.localeCompare(b)) : undefined;
  const confers = body.confers ? [...body.confers].map((c) => `${c.kind}:${c.slug}`).sort((a, b) => a.localeCompare(b)) : undefined;
  return JSON.stringify({ ...body, features, confers });
}

function baselineBody(i: Item): DefineItemRequestBody {
  if (i.type === 'module') return moduleBody({ slug: '', features: i.features ?? [], requiresTier: i.requiresTier ?? null, status: readStatus(i) });
  if (i.type === 'trial-offer') return offerBody({ slug: '', confers: (i.confers ?? []).map((c) => ({ kind: c.kind ?? '', slug: c.slug ?? '' })), durationDays: i.durationDays == null ? null : Number(i.durationDays), availability: i.availability ?? null, oncePerOrg: i.oncePerOrg ?? false });
  return tierBody({ slug: '', features: i.features ?? [], status: readStatus(i) });
}

// The single inclusion decision. Both toWrites and summarise (Task 2) derive from this,
// so the reviewed summary and the actual writes cannot diverge. Order-insensitive.
export function diffCatalogue(draft: Draft, baseline: Item[]): ChangedItem[] {
  const byId = new Map(baseline.map((i) => [i.id, i]));
  const out: ChangedItem[] = [];
  const consider = (id: string, kind: Kind, draftBody: DefineItemRequestBody) => {
    const base = byId.get(id) ?? null;
    if (base && norm(baselineBody(base)) === norm(draftBody)) return;
    out.push({ id, kind, draftBody, base });
  };
  for (const t of draft.tiers) consider(`tier:${t.slug}`, 'tier', tierBody(t));
  for (const m of draft.modules) consider(`module:${m.slug}`, 'module', moduleBody(m));
  for (const o of draft.offers) consider(`trial-offer:${o.slug}`, 'trial-offer', offerBody(o));
  return out.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
}

// version from BASELINE (authoritative for concurrency), never the draft. New item -> create.
export function toWrites(draft: Draft, baseline: Item[]): CatalogueWrite[] {
  return diffCatalogue(draft, baseline).map((c) => ({ id: c.id, productId: draft.productId, version: c.base?.version, body: c.draftBody }));
}
